import { useEffect, useState } from "react";
import { SITE, url } from "@/data/site";
import PageShell from "@/components/PageShell";
import BriefForm from "@/components/BriefForm";
import ContactModal from "@/components/ContactModal";
import { PageTitle, Section } from "@/components/shared/Primitives";
import { PROCESS } from "@/data/studio";
import { track, EVENTS } from "@/lib/analytics";

const CANONICAL = url("/start-a-project");

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ContactPage",
      name: "Start a Project — Elentara",
      url: CANONICAL,
      description:
        "Send Elentara a project brief for immersive web, creative technology, 3D, spatial computing or interactive systems work.",
      isPartOf: { "@id": `${SITE.base}/#website` },
      about: { "@id": `${SITE.base}/#organization` },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Elentara", item: url("/") },
        { "@type": "ListItem", position: 2, name: "Start a Project", item: CANONICAL },
      ],
    },
  ],
};

const START_SEO = {
  title: "Start a Project — Send Elentara Your Brief | Creative Technology Studio",
  description:
    "Tell Elentara what should exist next. Share your project brief for immersive web, creative technology, 3D and motion, WebAR or interactive data work.",
  canonical: CANONICAL,
  jsonLd: JSON_LD,
};

export default function StartProject() {
  const [quickOpen, setQuickOpen] = useState(false);

  useEffect(() => {
    track(EVENTS.START_PROJECT_CLICK, { from: "page_view" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `track`/`EVENTS` are module imports
  }, []);

  return (
    <PageShell
      testId="start-project-page"
      showFinalCta={false}
      seo={START_SEO}
    >
      <Section testId="start-project-hero" className="!pt-36 !pb-12">
        <PageTitle
          overline="Start a Project"
          testId="start-project-title"
          title="What should exist next?"
          lead="Have an idea that doesn't fit the usual brief? Tell us as much or as little as you know — the first three fields are enough to begin a conversation."
        />
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <a
            href={`mailto:${SITE.email}`}
            data-testid="start-project-email-link"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#06B6D4] transition-colors duration-300 hover:text-[#F4F6FB]"
          >
            {SITE.email}
          </a>
          <button
            type="button"
            data-testid="start-project-quick-button"
            onClick={() => setQuickOpen(true)}
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F4F6FB]/45 transition-colors duration-300 hover:text-[#F4F6FB]"
          >
            Prefer a short message? →
          </button>
        </div>
      </Section>

      <Section testId="start-project-form" className="!pt-0">
        <div className="grid gap-14 lg:grid-cols-[2fr_1fr] lg:gap-20">
          <BriefForm />
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-white/10 bg-[#12141C]/45 p-8 backdrop-blur-xl">
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#06B6D4]">
                What happens next
              </p>
              <ol className="mt-7 space-y-6">
                {PROCESS.map((stage) => (
                  <li key={stage.id} className="flex gap-4">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-[#D4AF37]">
                      {stage.id}
                    </span>
                    <span>
                      <span className="font-display block text-sm font-semibold uppercase tracking-[0.12em] text-[#F4F6FB]">
                        {stage.title}
                      </span>
                      <span className="mt-1.5 block text-xs leading-relaxed text-[#F4F6FB]/50">
                        {stage.content}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
            <p className="mt-6 px-2 text-xs leading-relaxed text-[#F4F6FB]/35">
              Your brief is sent directly to the studio inbox and stored privately. Attachments are
              held in secure storage — nothing is shared with third parties.
            </p>
          </aside>
        </div>
      </Section>

      <ContactModal open={quickOpen} onClose={() => setQuickOpen(false)} />
    </PageShell>
  );
}
